import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useSearchParams, useParams } from 'react-router-dom';
import { useForm, useWatch } from 'react-hook-form';
import {
  ChevronLeft, Save, FileDown, Calculator, UserPlus, MessageSquare,
  Sparkles, Loader, ToggleLeft, ToggleRight, Calendar, Copy, RotateCcw, Mail,
} from 'lucide-react';
import { devisApi, enfantsApi, tarifsApi, demandesApi, parametresApi } from '../../utils/api';
import { useApp } from '../../context/AppContext';
import { useAuth } from '../../context/AuthContext';
import { timeToDecimal, formatMontant, getHeuresLissees } from '../../utils/calculs';
import { openPdf, apercuDisponible } from '../../components/PdfPreview';

// Âge en années à une date de référence
function ageAnnees(dateNaissance, refDate) {
  if (!dateNaissance) return null;
  const dn = new Date(dateNaissance + (dateNaissance.length <= 10 ? 'T00:00:00' : ''));
  const ref = refDate ? new Date(refDate + 'T00:00:00') : new Date();
  if (isNaN(dn) || isNaN(ref)) return null;
  let a = ref.getFullYear() - dn.getFullYear();
  const m = ref.getMonth() - dn.getMonth();
  if (m < 0 || (m === 0 && ref.getDate() < dn.getDate())) a--;
  return a;
}

// CMG micro-crèche mensuel d'après le barème actif, le revenu fiscal, le nb d'enfants à charge et l'âge.
function calculerCMG(b, rfr, nbEnf, age) {
  if (!b || !rfr || rfr <= 0 || age == null || age >= 6) return null;
  const N = Math.min(Math.max(parseInt(nbEnf, 10) || 1, 1), 3);
  const bas = b[`plafond_bas_${N}`], haut = b[`plafond_haut_${N}`];
  let t = 3;
  if (bas != null && rfr <= bas) t = 1;
  else if (haut != null && rfr <= haut) t = 2;
  const ageKey = age < 3 ? 'm3' : '36';
  const montant = b[`cmg_${ageKey}_t${t}`] || 0;
  return { montant, tranche: t, ageKey, N };
}

const DAYS = [
  { key: 'lundi', label: 'Lundi' },
  { key: 'mardi', label: 'Mardi' },
  { key: 'mercredi', label: 'Mercredi' },
  { key: 'jeudi', label: 'Jeudi' },
  { key: 'vendredi', label: 'Vendredi' },
];

function calcFromForm(values) {
  let total = 0;
  for (const day of DAYS) {
    if (values[`${day.key}_actif`] && values[`${day.key}_debut`] && values[`${day.key}_fin`]) {
      const d = timeToDecimal(values[`${day.key}_debut`]);
      const f = timeToDecimal(values[`${day.key}_fin`]);
      if (f > d) total += f - d;
    }
  }
  return total;
}

function getJoursSouhaites(values) {
  return DAYS.filter(d => values[`${d.key}_actif`]).map(d => d.label).join(', ');
}

// Inline mini form for registering a new family
function NouvellesFamilleForm({ onCreated, structures }) {
  const [form, setForm] = useState({
    enfant_nom: '', enfant_prenom: '', date_naissance: '', sexe: '',
    structure_id: '',
    parent1_nom: '', parent1_prenom: '', parent1_tel: '', parent1_email: '',
    parent1_lien: 'Mère',
    parent2_nom: '', parent2_prenom: '', parent2_tel: '', parent2_email: '',
    parent2_lien: 'Père',
  });
  const [saving, setSaving] = useState(false);
  const upd = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSave = async () => {
    if (!form.enfant_nom || !form.enfant_prenom) {
      alert('Le nom et le prénom de l\'enfant sont requis.');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        nom: form.enfant_nom,
        prenom: form.enfant_prenom,
        date_naissance: form.date_naissance || null,
        sexe: form.sexe || null,
        structure_id: form.structure_id || null,
        statut: 'en_attente',
        parents: [],
      };
      if (form.parent1_nom || form.parent1_prenom) {
        payload.parents.push({
          type: 'parent1', nom: form.parent1_nom, prenom: form.parent1_prenom,
          lien: form.parent1_lien, telephone_portable: form.parent1_tel, email: form.parent1_email,
        });
      }
      if (form.parent2_nom || form.parent2_prenom) {
        payload.parents.push({
          type: 'parent2', nom: form.parent2_nom, prenom: form.parent2_prenom,
          lien: form.parent2_lien, telephone_portable: form.parent2_tel, email: form.parent2_email,
        });
      }
      const res = await enfantsApi.create(payload);
      onCreated(res.data.id, `${form.enfant_prenom} ${form.enfant_nom}`, form.structure_id);
    } catch (err) {
      alert('Erreur : ' + (err.response?.data?.error || err.message));
    }
    setSaving(false);
  };

  return (
    <div className="card p-5 border-2 border-navy-200 bg-navy-50/30 space-y-4">
      <h4 className="font-semibold text-navy-700 flex items-center gap-2">
        <UserPlus size={16} /> Enregistrer une nouvelle famille
      </h4>

      {/* Enfant */}
      <div>
        <div className="text-xs font-semibold text-gray-500 uppercase mb-2">Enfant</div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <input className="form-input" placeholder="Prénom *" value={form.enfant_prenom} onChange={e => upd('enfant_prenom', e.target.value)} />
          <input className="form-input" placeholder="Nom *" value={form.enfant_nom} onChange={e => upd('enfant_nom', e.target.value)} />
          <input type="date" className="form-input" placeholder="Date naissance" value={form.date_naissance} onChange={e => upd('date_naissance', e.target.value)} />
          <select className="form-select" value={form.sexe} onChange={e => upd('sexe', e.target.value)}>
            <option value="">Sexe</option>
            <option value="M">Masculin</option>
            <option value="F">Féminin</option>
          </select>
        </div>
        <div className="mt-2">
          <select className="form-select w-64" value={form.structure_id} onChange={e => upd('structure_id', e.target.value)}>
            <option value="">Structure d'accueil</option>
            {structures.map(s => <option key={s.id} value={s.id}>{s.nom}</option>)}
          </select>
        </div>
      </div>

      {/* Parent 1 */}
      <div>
        <div className="text-xs font-semibold text-gray-500 uppercase mb-2">Parent 1</div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <input className="form-input" placeholder="Prénom" value={form.parent1_prenom} onChange={e => upd('parent1_prenom', e.target.value)} />
          <input className="form-input" placeholder="Nom" value={form.parent1_nom} onChange={e => upd('parent1_nom', e.target.value)} />
          <input className="form-input" placeholder="Téléphone" value={form.parent1_tel} onChange={e => upd('parent1_tel', e.target.value)} />
          <input className="form-input" placeholder="Email" value={form.parent1_email} onChange={e => upd('parent1_email', e.target.value)} />
        </div>
      </div>

      {/* Parent 2 */}
      <div>
        <div className="text-xs font-semibold text-gray-500 uppercase mb-2">Parent 2 (optionnel)</div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <input className="form-input" placeholder="Prénom" value={form.parent2_prenom} onChange={e => upd('parent2_prenom', e.target.value)} />
          <input className="form-input" placeholder="Nom" value={form.parent2_nom} onChange={e => upd('parent2_nom', e.target.value)} />
          <input className="form-input" placeholder="Téléphone" value={form.parent2_tel} onChange={e => upd('parent2_tel', e.target.value)} />
          <input className="form-input" placeholder="Email" value={form.parent2_email} onChange={e => upd('parent2_email', e.target.value)} />
        </div>
      </div>

      <button type="button" onClick={handleSave} disabled={saving}
        className="btn-primary">
        {saving ? <Loader size={14} className="animate-spin" /> : <Save size={14} />}
        {saving ? 'Enregistrement...' : 'Enregistrer la famille'}
      </button>
    </div>
  );
}

export default function DevisForm() {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = !!id;
  const [searchParams] = useSearchParams();
  const { structures } = useApp();
  const { canSeeFinancials } = useAuth();
  const [enfants, setEnfants] = useState([]);
  const [saving, setSaving] = useState(false);
  const [showNewFamily, setShowNewFamily] = useState(false);
  const [tarifs, setTarifs] = useState([]);
  const [modeTarification, setModeTarification] = useState('horaire');
  const [tarifPerso, setTarifPerso] = useState(null);   // null = tarif auto (grille) ; sinon override €/h
  const [loadedTarif, setLoadedTarif] = useState(null); // tarif enregistré à restaurer en édition
  const [montantForfait, setMontantForfait] = useState('');
  const [bareme, setBareme] = useState(null);
  const [lockedEnfant, setLockedEnfant] = useState(null); // enfant imposé (venant d'une demande)

  // Georges analyse
  const [analyse, setAnalyse] = useState('');
  const [analyseLoading, setAnalyseLoading] = useState(false);

  const prefillEnfantId = searchParams.get('enfant_id');

  const { register, handleSubmit, control, setValue, watch, reset } = useForm({
    defaultValues: {
      structure_id: '',
      enfant_id: prefillEnfantId || '',
      date_devis: new Date().toISOString().split('T')[0],
      date_debut_contrat: '',
      lundi_actif: false, lundi_debut: '08:00', lundi_fin: '18:00',
      mardi_actif: false, mardi_debut: '08:00', mardi_fin: '18:00',
      mercredi_actif: false, mercredi_debut: '08:00', mercredi_fin: '18:00',
      jeudi_actif: false, jeudi_debut: '08:00', jeudi_fin: '18:00',
      vendredi_actif: false, vendredi_debut: '08:00', vendredi_fin: '18:00',
      notes: '',
      commentaires: '',
      revenu_fiscal_reference: '',
      nb_enfants_charge: 1,
    },
  });

  const watchedValues = useWatch({ control });

  const loadEnfants = useCallback(() => {
    enfantsApi.getAll({}).then(res => setEnfants(res.data)).catch(console.error);
  }, []);

  useEffect(() => {
    loadEnfants();
    tarifsApi.getAll({ actif: true }).then(res => setTarifs(res.data)).catch(console.error);
    parametresApi.getBareme().then(res => setBareme(res.data)).catch(() => {});
  }, [loadEnfants]);

  // Enfant imposé (devis créé depuis une demande) : on le verrouille et on récupère sa fiche
  useEffect(() => {
    if (isEdit || !prefillEnfantId) return;
    setValue('enfant_id', String(prefillEnfantId));
    enfantsApi.getById(prefillEnfantId).then(({ data: e }) => {
      setLockedEnfant(e);
      if (e.structure_id) setValue('structure_id', String(e.structure_id));
    }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEdit, prefillEnfantId]);

  // Édition d'un devis existant : pré-remplissage du formulaire
  useEffect(() => {
    if (!isEdit) return;
    devisApi.getById(id).then(({ data: d }) => {
      const days = {};
      for (const day of DAYS) {
        const deb = d[`${day.key}_debut`], fin = d[`${day.key}_fin`];
        days[`${day.key}_actif`] = !!(deb && fin);
        days[`${day.key}_debut`] = deb || '08:00';
        days[`${day.key}_fin`] = fin || '18:00';
      }
      reset({
        structure_id: d.structure_id ? String(d.structure_id) : '',
        enfant_id: d.enfant_id ? String(d.enfant_id) : '',
        date_devis: (d.date_devis || '').slice(0, 10) || new Date().toISOString().split('T')[0],
        date_debut_contrat: (d.date_debut_contrat || '').slice(0, 10) || '',
        notes: d.notes || '',
        commentaires: d.commentaires || '',
        remise: d.remise || '',
        remise_motif: d.remise_motif || '',
        revenu_fiscal_reference: d.revenu_fiscal_reference || '',
        nb_enfants_charge: d.nb_enfants_charge || 1,
        ...days,
      });
      setModeTarification(d.mode_tarification || 'horaire');
      setMontantForfait(d.montant_forfait ? String(d.montant_forfait) : '');
      setLoadedTarif(d.mode_tarification !== 'forfait' && d.tarif_horaire != null ? Number(d.tarif_horaire) : null);
    }).catch(() => {});
  }, [isEdit, id, reset]);

  // Pré-remplissage depuis une DEMANDE entrante (« Faire un devis »)
  const prefillDemandeId = searchParams.get('demande_id');
  useEffect(() => {
    if (isEdit || !prefillDemandeId) return;
    demandesApi.getById(prefillDemandeId).then(({ data: dem }) => {
      if (dem.structure_id) setValue('structure_id', String(dem.structure_id));
      if (dem.date_entree_souhaitee) setValue('date_debut_contrat', String(dem.date_entree_souhaitee).slice(0, 10));
      let jours = dem.jours_souhaites;
      if (typeof jours === 'string') { try { jours = JSON.parse(jours); } catch { jours = []; } }
      (jours || []).forEach(j => {
        const key = (j.jour || '').toLowerCase();
        if (DAYS.some(d => d.key === key)) {
          setValue(`${key}_actif`, true);
          if (j.debut) setValue(`${key}_debut`, j.debut);
          if (j.fin) setValue(`${key}_fin`, j.fin);
        }
      });
    }).catch(() => {});
  }, [isEdit, prefillDemandeId, setValue]);

  const heuresHebdo = calcFromForm(watchedValues);
  const heuresLissees = getHeuresLissees(heuresHebdo);

  // Tarif auto déduit de la grille (par tranche d'heures)
  const matchingTarif = tarifs.find(t =>
    t.type === 'horaire' && heuresHebdo >= t.heures_min && heuresHebdo <= t.heures_max
  );
  const tarifAuto = matchingTarif ? matchingTarif.tarif : 0;
  // Override éventuel : tarif personnalisé choisi par l'utilisateur
  const tarifPersoNum = (tarifPerso !== null && tarifPerso !== '' && !isNaN(parseFloat(tarifPerso)))
    ? parseFloat(tarifPerso) : null;
  const tarifPersoActif = tarifPersoNum != null && tarifPersoNum !== tarifAuto;
  const tarifHoraire = tarifPersoNum != null ? tarifPersoNum : tarifAuto;

  // En édition : si le tarif enregistré diffère de l'auto, on le restaure comme tarif personnalisé
  useEffect(() => {
    if (loadedTarif == null || !tarifs.length) return;
    const m = tarifs.find(t => t.type === 'horaire' && heuresHebdo >= t.heures_min && heuresHebdo <= t.heures_max);
    const auto = m ? m.tarif : 0;
    if (Number(loadedTarif) !== Number(auto)) setTarifPerso(String(loadedTarif));
    setLoadedTarif(null);
  }, [loadedTarif, tarifs, heuresHebdo]);

  const montantMensuel = modeTarification === 'forfait'
    ? (parseFloat(montantForfait) || 0)
    : heuresLissees * tarifHoraire;

  // CMG micro-crèche estimé selon le revenu fiscal de référence + nb d'enfants à charge + âge de l'enfant
  const enfantLie = lockedEnfant || enfants.find(e => String(e.id) === String(watchedValues.enfant_id)) || null;
  const ageEnf = ageAnnees(enfantLie?.date_naissance, watchedValues.date_debut_contrat);
  const cmg = calculerCMG(bareme, parseFloat(watchedValues.revenu_fiscal_reference), watchedValues.nb_enfants_charge, ageEnf);
  const resteACharge = cmg ? Math.max(0, montantMensuel - cmg.montant) : null;
  const estVerrouille = !isEdit && !!prefillEnfantId;

  // Duplique les horaires d'un jour (ou du 1er jour coché) sur tous les jours de la semaine
  const dupliquerHoraires = (srcKey) => {
    const src = srcKey || DAYS.find(d => watchedValues[`${d.key}_actif`])?.key || 'lundi';
    const deb = watchedValues[`${src}_debut`] || '08:00';
    const fin = watchedValues[`${src}_fin`] || '18:00';
    DAYS.forEach(d => { setValue(`${d.key}_actif`, true); setValue(`${d.key}_debut`, deb); setValue(`${d.key}_fin`, fin); });
  };

  const handleFamilyCreated = (enfantId, label, structureId) => {
    loadEnfants();
    setValue('enfant_id', String(enfantId));
    if (structureId) setValue('structure_id', String(structureId));
    setShowNewFamily(false);
  };

  const handleAnalyse = async () => {
    setAnalyseLoading(true);
    setAnalyse('');
    try {
      const res = await devisApi.analyseRentabilite({
        heures_hebdo: heuresHebdo,
        tarif_horaire: tarifHoraire,
        montant_mensuel: montantMensuel,
        structure_id: watchedValues.structure_id || null,
        mode_tarification: modeTarification,
        montant_forfait: parseFloat(montantForfait) || 0,
      });
      setAnalyse(res.data.analyse);
    } catch (err) {
      setAnalyse('Impossible de charger l\'analyse. Vérifiez que les données du devis sont complètes.');
    }
    setAnalyseLoading(false);
  };

  const onSubmit = async (data, generatePdf = false, pdfWin = null, goSend = false) => {
    setSaving(true);
    try {
      const payload = {
        structure_id: data.structure_id || null,
        enfant_id: data.enfant_id || null,
        date_devis: data.date_devis,
        date_debut_contrat: data.date_debut_contrat || null,
        notes: data.notes || null,
        commentaires: data.commentaires || null,
        heures_hebdo: heuresHebdo,
        mode_tarification: modeTarification,
        tarif_horaire: tarifHoraire,
        montant_forfait: parseFloat(montantForfait) || 0,
        remise: parseFloat(data.remise) || 0,
        remise_motif: (data.remise_motif || '').trim() || null,
        jours_souhaites: getJoursSouhaites(data),
        revenu_fiscal_reference: parseFloat(data.revenu_fiscal_reference) || null,
        nb_enfants_charge: parseInt(data.nb_enfants_charge, 10) || 1,
        cmg_estime: cmg ? cmg.montant : null,
      };
      for (const day of DAYS) {
        payload[`${day.key}_debut`] = data[`${day.key}_actif`] ? data[`${day.key}_debut`] : null;
        payload[`${day.key}_fin`] = data[`${day.key}_actif`] ? data[`${day.key}_fin`] : null;
      }
      const res = isEdit ? await devisApi.update(id, payload) : await devisApi.create(payload);
      const devisId = isEdit ? id : res.data.id;
      if (generatePdf) {
        const url = devisApi.getPdfUrl(devisId);
        // L'onglet a été ouvert dans le clic (évite le blocage pop-up) : on le dirige vers le PDF.
        if (pdfWin && !pdfWin.closed) pdfWin.location = url;
        else openPdf(url, 'Devis');
      }
      navigate(goSend ? `/devis/${devisId}?envoyer=1` : `/devis/${devisId}`); // fiche du devis (Envoyer, PDF, signature)
    } catch (err) {
      if (pdfWin && !pdfWin.closed) pdfWin.close();
      console.error(err);
      alert(err.response?.data?.error || (isEdit ? 'Erreur lors de la modification du devis' : 'Erreur lors de la création du devis'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={handleSubmit(d => onSubmit(d, false))} className="space-y-6 max-w-3xl">
      {/* Header */}
      <div className="flex items-center gap-3">
        <button type="button" onClick={() => navigate('/devis')} className="btn-secondary">
          <ChevronLeft size={16} /> Retour
        </button>
        <h2 className="text-lg font-bold text-gray-800">{isEdit ? 'Modifier le devis' : 'Nouveau devis'}</h2>
      </div>

      {/* Famille : sélection ou création */}
      <div className="card p-6 space-y-4">
        <div className="flex items-center justify-between border-b pb-2">
          <h3 className="font-semibold text-gray-700">Famille & Enfant</h3>
          {!estVerrouille && (
            <button type="button" onClick={() => setShowNewFamily(!showNewFamily)}
              className="text-sm text-navy-700 hover:text-navy-800 flex items-center gap-1">
              <UserPlus size={14} />
              {showNewFamily ? 'Sélectionner un enfant existant' : 'Nouvelle famille'}
            </button>
          )}
        </div>

        {estVerrouille ? (
          // Enfant imposé par la demande : pas de sélection
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="form-label">Enfant</label>
              <div className="form-input bg-gray-50 flex items-center gap-2 text-navy-800 font-medium">
                {enfantLie ? `${enfantLie.prenom || ''} ${enfantLie.nom || ''}`.trim() : 'Chargement…'}
                <span className="text-[11px] text-gray-400">(issu de la demande)</span>
              </div>
              <input type="hidden" {...register('enfant_id')} />
            </div>
            <div>
              <label className="form-label">Structure</label>
              <div className="form-input bg-gray-50 text-navy-800 font-medium">
                {structures.find(s => String(s.id) === String(watchedValues.structure_id))?.nom || '—'}
              </div>
              <input type="hidden" {...register('structure_id')} />
            </div>
          </div>
        ) : showNewFamily ? (
          <NouvellesFamilleForm onCreated={handleFamilyCreated} structures={structures} />
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="form-label">Enfant</label>
              <select {...register('enfant_id')} className="form-select">
                <option value="">-- Sélectionner un enfant --</option>
                {enfants.map(e => (
                  <option key={e.id} value={e.id}>{e.prenom} {e.nom}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="form-label">Structure</label>
              <select {...register('structure_id')} className="form-select">
                <option value="">-- Sélectionner --</option>
                {structures.map(s => (
                  <option key={s.id} value={s.id}>{s.nom}</option>
                ))}
              </select>
            </div>
          </div>
        )}

        {/* Aide CMG (micro-crèche) selon le revenu fiscal de référence */}
        <div className="border-t pt-4">
          <div className="text-xs font-semibold text-gray-500 uppercase mb-2">Aide CMG (micro-crèche)</div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
            <div>
              <label className="form-label">Revenu fiscal de référence (N-1)</label>
              <input type="number" step="1" {...register('revenu_fiscal_reference')} className="form-input" placeholder="ex : 32000" />
            </div>
            <div>
              <label className="form-label">Enfants à charge</label>
              <select {...register('nb_enfants_charge')} className="form-select">
                <option value="1">1 enfant</option>
                <option value="2">2 enfants</option>
                <option value="3">3 enfants ou plus</option>
              </select>
            </div>
            <div className="text-sm">
              {!bareme ? <span className="text-gray-400">Barème indisponible.</span>
                : !watchedValues.revenu_fiscal_reference ? <span className="text-gray-400">Saisissez le revenu fiscal pour estimer le CMG.</span>
                  : ageEnf == null ? <span className="text-amber-600">Date de naissance de l'enfant manquante.</span>
                    : ageEnf >= 6 ? <span className="text-gray-500">Enfant de 6 ans ou plus : pas de CMG.</span>
                      : cmg ? (
                        <div className="rounded-lg bg-emerald-50 border border-emerald-200 px-3 py-2">
                          <div className="text-emerald-700 font-semibold">CMG estimé : {formatMontant(cmg.montant)}/mois</div>
                          <div className="text-[11px] text-emerald-600">Tranche {cmg.tranche} · enfant {cmg.ageKey === 'm3' ? '< 3 ans' : '3–6 ans'}</div>
                        </div>
                      ) : null}
            </div>
          </div>
          {cmg && canSeeFinancials && (
            <div className="mt-2 text-[12.5px] text-gray-500">
              Coût mensuel lissé : <strong>{formatMontant(montantMensuel)}</strong> · CMG estimé : <strong className="text-emerald-700">− {formatMontant(cmg.montant)}</strong> · Reste à charge estimé : <strong>{formatMontant(resteACharge)}/mois</strong>
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="form-label">Date du devis</label>
            <input type="date" {...register('date_devis')} className="form-input" />
          </div>
          <div>
            <label className="form-label">Date d'entrée prévisionnelle</label>
            <input type="date" {...register('date_debut_contrat')} className="form-input" />
          </div>
        </div>
      </div>

      {/* Mode de tarification */}
      <div className="card p-6">
        <h3 className="font-semibold text-gray-700 border-b pb-2 mb-4">Mode de tarification</h3>
        <div className="flex items-center gap-4">
          <button type="button" onClick={() => setModeTarification('horaire')}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              modeTarification === 'horaire'
                ? 'bg-navy-100 text-navy-700 ring-2 ring-navy-300'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}>
            {modeTarification === 'horaire' ? <ToggleRight size={16} /> : <ToggleLeft size={16} />}
            Tarif horaire (grille)
          </button>
          <button type="button" onClick={() => setModeTarification('forfait')}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              modeTarification === 'forfait'
                ? 'bg-amber-100 text-amber-700 ring-2 ring-amber-300'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}>
            {modeTarification === 'forfait' ? <ToggleRight size={16} /> : <ToggleLeft size={16} />}
            Forfait mensuel
          </button>
        </div>

        {modeTarification === 'forfait' && (
          <div className="mt-4">
            <label className="form-label">Montant forfaitaire mensuel</label>
            <div className="flex items-center gap-2">
              <input
                type="number"
                step="0.01"
                value={montantForfait}
                onChange={e => setMontantForfait(e.target.value)}
                className="form-input w-48"
                placeholder="Ex: 850.00"
              />
              <span className="text-gray-500">€/mois</span>
            </div>
          </div>
        )}

        {modeTarification === 'horaire' && (
          <div className="mt-4 bg-gray-50 rounded-lg p-3">
            <div className="flex items-center justify-between mb-2 gap-2">
              <div className="text-xs font-semibold text-gray-500 uppercase">Tarif horaire — cliquez pour choisir une autre ligne</div>
              {tarifPersoActif && (
                <button type="button" onClick={() => setTarifPerso(null)}
                  className="text-[11px] text-navy-700 hover:text-navy-900 flex items-center gap-1 flex-shrink-0">
                  <RotateCcw size={12} /> Tarif auto ({formatMontant(tarifAuto)})
                </button>
              )}
            </div>
            {tarifs.filter(t => t.type === 'horaire').length > 0 && (
              <div className="grid grid-cols-4 gap-1 text-xs">
                {tarifs.filter(t => t.type === 'horaire').map(t => {
                  const sel = Number(t.tarif) === Number(tarifHoraire);
                  return (
                    <button type="button" key={t.id} onClick={() => setTarifPerso(String(t.tarif))}
                      className={`px-2 py-1 rounded text-center transition-colors ${
                        sel
                          ? 'bg-navy-100 text-navy-700 font-bold ring-1 ring-navy-300'
                          : 'bg-white text-gray-600 hover:bg-navy-50'
                      }`}>
                      {t.label}: {t.tarif}€
                    </button>
                  );
                })}
              </div>
            )}
            <div className="flex items-center gap-2 mt-3 flex-wrap">
              <label className="text-[11px] text-gray-500">Ou tarif personnalisé</label>
              <input type="number" step="0.01" min="0"
                value={tarifPerso ?? ''}
                placeholder={tarifAuto ? String(tarifAuto) : '—'}
                onChange={e => setTarifPerso(e.target.value)}
                className="form-input w-24 h-8 text-sm" />
              <span className="text-gray-500 text-sm">€/h</span>
              {tarifPersoActif && (
                <span className="text-[11px] text-amber-600 font-medium">Tarif choisi manuellement (remplace la grille)</span>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Planning */}
      <div className="card p-6">
        <div className="flex items-center justify-between border-b pb-2 mb-4">
          <h3 className="font-semibold text-gray-700">Planning hebdomadaire</h3>
          <button type="button" onClick={() => dupliquerHoraires()}
            className="text-xs text-navy-700 hover:text-navy-900 flex items-center gap-1 border border-navy-200 rounded-lg px-2.5 py-1.5"
            title="Appliquer les horaires du 1er jour coché à tous les jours">
            <Calendar size={13} /> Mêmes horaires tous les jours
          </button>
        </div>
        <div className="space-y-3">
          {DAYS.map(day => (
            <div key={day.key} className="flex items-center gap-3">
              <div className="flex items-center gap-2 w-28">
                <input
                  type="checkbox"
                  id={`${day.key}_actif`}
                  {...register(`${day.key}_actif`)}
                  className="w-4 h-4 accent-primary-500"
                />
                <label htmlFor={`${day.key}_actif`} className="text-sm font-medium text-gray-700">
                  {day.label}
                </label>
              </div>
              <input
                type="time"
                {...register(`${day.key}_debut`)}
                className="form-input w-28"
                disabled={!watchedValues[`${day.key}_actif`]}
              />
              <span className="text-gray-400 text-sm">&rarr;</span>
              <input
                type="time"
                {...register(`${day.key}_fin`)}
                className="form-input w-28"
                disabled={!watchedValues[`${day.key}_actif`]}
              />
              {watchedValues[`${day.key}_actif`] &&
               watchedValues[`${day.key}_debut`] &&
               watchedValues[`${day.key}_fin`] && (
                <span className="text-xs text-gray-500">
                  {(timeToDecimal(watchedValues[`${day.key}_fin`]) - timeToDecimal(watchedValues[`${day.key}_debut`])).toFixed(1)}h
                </span>
              )}
              {watchedValues[`${day.key}_actif`] && (
                <button type="button" onClick={() => dupliquerHoraires(day.key)}
                  className="text-gray-300 hover:text-navy-600 ml-auto" title={`Copier les horaires du ${day.label.toLowerCase()} sur tous les jours`}>
                  <Copy size={14} />
                </button>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Auto-calculated summary */}
      <div className="card p-6 bg-primary-50 border-primary-100">
        <div className="flex items-center gap-2 mb-4">
          <Calculator size={18} className="text-primary-500" />
          <h3 className="font-semibold text-primary-700">Récapitulatif calculé</h3>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-white rounded-lg p-3 text-center">
            <div className="text-xl font-bold text-gray-900">{heuresHebdo.toFixed(1)}h</div>
            <div className="text-xs text-gray-500 mt-1">Heures/semaine</div>
          </div>
          <div className="bg-white rounded-lg p-3 text-center">
            <div className="text-xl font-bold text-gray-900">{heuresLissees.toFixed(1)}h</div>
            <div className="text-xs text-gray-500 mt-1">Heures lissées/mois</div>
          </div>
          <div className="bg-white rounded-lg p-3 text-center">
            <div className="text-xl font-bold text-gray-900">
              {modeTarification === 'forfait' ? 'Forfait' : formatMontant(tarifHoraire)}
            </div>
            <div className="text-xs text-gray-500 mt-1">
              {modeTarification === 'forfait' ? 'Mode forfait' : 'Tarif horaire'}
            </div>
          </div>
          <div className="bg-white rounded-lg p-3 text-center border-2 border-primary-200">
            <div className="text-2xl font-bold text-primary-600">{formatMontant(montantMensuel)}</div>
            <div className="text-xs text-primary-500 mt-1 font-medium">Mensualité</div>
          </div>
        </div>
        <div className="mt-3 flex items-center justify-between text-sm text-primary-600">
          <span>Arrhes à verser (1 mois) : <strong>{formatMontant(montantMensuel)}</strong></span>
          <span>Frais de dossier : <strong>{formatMontant(100)}</strong></span>
          <span>Heures supp : <strong>10,00 €/h</strong></span>
        </div>
        {/* Remise éventuelle */}
        <div className="mt-3 grid grid-cols-12 gap-2 items-end bg-white/60 rounded-lg p-2">
          <div className="col-span-3">
            <label className="text-[11px] text-gray-500">Remise (€)</label>
            <input type="number" step="0.01" {...register('remise')} className="form-input" placeholder="0" />
          </div>
          <div className="col-span-6">
            <label className="text-[11px] text-gray-500">Motif de la remise</label>
            <input type="text" {...register('remise_motif')} className="form-input" placeholder="ex. fratrie, geste commercial…" />
          </div>
          <div className="col-span-3 text-right">
            <div className="text-[11px] text-gray-500">Net à payer / mois</div>
            <div className="text-lg font-bold text-primary-700">{formatMontant(Math.max(0, montantMensuel - (parseFloat(watchedValues.remise) || 0)))}</div>
          </div>
        </div>
        <div className="mt-2 text-xs text-primary-400">
          {modeTarification === 'forfait'
            ? `Forfait mensuel fixe · Sans TVA (art. 293 CGI)`
            : `Calcul : ${heuresHebdo.toFixed(2)}h × 47 sem. ÷ 12 mois × ${formatMontant(tarifHoraire)}/h · Sans TVA (art. 293 CGI)`
          }
        </div>
      </div>

      {/* Georges Analyse Rentabilité */}
      {canSeeFinancials && (
      <div className="card p-6 border-gold-200">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Sparkles size={18} className="text-gold-500" />
            <h3 className="font-semibold text-gold-700">Avis de Georges - Rentabilité</h3>
          </div>
          <button type="button" onClick={handleAnalyse} disabled={analyseLoading || heuresHebdo === 0}
            className="btn-secondary text-xs px-3 py-1.5 flex items-center gap-1.5">
            {analyseLoading ? <Loader size={12} className="animate-spin" /> : <Sparkles size={12} />}
            {analyseLoading ? 'Analyse en cours...' : 'Demander l\'avis de Georges'}
          </button>
        </div>
        {analyse ? (
          <div className="bg-gold-50 rounded-lg p-4 text-sm text-navy-800 whitespace-pre-wrap leading-relaxed">
            {analyse}
          </div>
        ) : (
          <p className="text-sm text-gray-400 italic">
            Remplissez le planning et cliquez pour obtenir l'analyse de rentabilité par Georges.
          </p>
        )}
      </div>
      )}

      {/* Commentaires / Impressions */}
      <div className="card p-6">
        <div className="flex items-center gap-2 mb-3">
          <MessageSquare size={16} className="text-amber-500" />
          <h3 className="font-semibold text-gray-700">Commentaires & Impressions sur la famille</h3>
        </div>
        <textarea
          {...register('commentaires')}
          className="form-input"
          rows={3}
          placeholder="Vos impressions sur l'échange avec la famille, ressenti, points d'attention..."
        />
        <p className="text-xs text-gray-400 mt-1">
          Usage interne uniquement. Ne sera pas imprimé sur le devis.
        </p>
      </div>

      {/* Notes */}
      <div className="card p-6">
        <label className="form-label">Notes (visibles sur le devis)</label>
        <textarea
          {...register('notes')}
          className="form-input"
          rows={3}
          placeholder="Notes ou remarques particulières..."
        />
      </div>

      {/* Buttons */}
      <div className="flex items-center gap-3 pb-6">
        <button type="submit" disabled={saving} className="btn-primary">
          <Save size={16} /> {saving ? 'Enregistrement...' : 'Enregistrer'}
        </button>
        <button
          type="button"
          disabled={saving}
          onClick={() => {
            // Sur mobile, l'onglet est ouvert dans le clic (sinon bloqué) ; ailleurs, aperçu intégré.
            const w = apercuDisponible() ? null : window.open('', '_blank');
            handleSubmit(
              d => onSubmit(d, true, w),
              () => { if (w && !w.closed) w.close(); }
            )();
          }}
          className="btn-secondary"
        >
          <FileDown size={16} /> Enregistrer et générer PDF
        </button>
        <button
          type="button"
          disabled={saving}
          onClick={handleSubmit(d => onSubmit(d, false, null, true))}
          className="btn-secondary"
        >
          <Mail size={16} /> Enregistrer et envoyer par email
        </button>
        <button type="button" onClick={() => navigate('/devis')} className="btn-secondary">
          Annuler
        </button>
      </div>
    </form>
  );
}
